import { getTaskDetails, getTaskVariables } from '@/api/backend/workflow';
import { ArrowBackFilled, CheckFilled, CloseFilled } from '@/assets/icons';
import { Button } from '@opaz/ui-components';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import FlowableTaskLayout from '../../-components/FlowableTaskLayout';
import { getInvoiceResponseForPaymentTask } from '../../-util/getInvoiceResponseForPaymentTask';
import { TaskPaymentLoading } from './-components/TaskPaymentLoading';
import { TaskDetailsType } from '@/api/backend/types.ts';
import { formatMoney } from '@/lib/utils';

export const Route = createFileRoute('/_auth/processes/$processInstanceId/task/$taskId/payment/')({
  component: RouteComponent,
  head: () => {
    return {
      meta: [
        {
          title: 'Payment - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

function RouteComponent() {
  const { taskId } = useParams({
    from: '/_auth/processes/$processInstanceId/task/$taskId/payment/',
  });
  const { t } = useTranslation();
  const [refetchCount, setRefetchCount] = useState(0);

  const {
    error: taskDetailsError,
    data: taskDetails,
    isLoading: isTaskDetailsLoading,
    isFetching: isTaskDetailsFetching,
  } = useQuery({
    queryKey: ['taskDetails', taskId],
    queryFn: async () => {
      const detailsResponse = await getTaskDetails(taskId);
      return detailsResponse;
    },
    refetchInterval: (query) => {
      if (query.state.dataUpdateCount >= 5) {
        return false;
      }

      if (query.state.data?.endTime != null) {
        return false;
      }

      return 2000;
    },
  });

  const { data: taskFormVariables, error: formVariablesError } = useQuery({
    queryKey: ['taskFormVariables', taskId],
    queryFn: async () => {
      const variablesResponse = await getTaskVariables(taskId);
      return variablesResponse;
    },
  });

  useEffect(() => {
    if (isTaskDetailsFetching && taskDetails) {
      setRefetchCount((prev) => prev + 1);
    }
  }, [isTaskDetailsFetching, taskDetails]);

  const shouldContinueRefetching = (data: TaskDetailsType | undefined, updateCount: number) => {
    return updateCount < 4 && (!data || data.endTime == null);
  };

  const showLoader = isTaskDetailsLoading || shouldContinueRefetching(taskDetails, refetchCount);

  if (taskDetailsError || formVariablesError) {
    return (
      <Layout>
        <div className="flex flex-col items-center gap-12">
          <FailureIcon />

          <div className="flex flex-col gap-6 items-center">
            <p className="font-bold text-2xl">{t('form.error.general.title')}</p>
            <p className="text-xl">{t('form.error.general.description')}</p>
          </div>
          <ActionButton title={t('payment.returnToApplication')} />
        </div>
      </Layout>
    );
  }

  if (showLoader) {
    return (
      <Layout>
        <TaskPaymentLoading />
      </Layout>
    );
  } else if (taskDetails && taskDetails.endTime != null) {
    const invoiceResponse = getInvoiceResponseForPaymentTask(taskDetails.taskDefinitionKey, taskFormVariables);
    const invoiceNumber = invoiceResponse?.invoiceNumber ?? null;
    const totalAmount = invoiceResponse?.totalAmount ?? null;

    return (
      <Layout>
        <div className="flex flex-col items-center gap-12">
          <SuccessIcon />

          <div className="flex flex-col gap-6">
            <p className="font-bold text-2xl">{t('payment.successMessage')}</p>
            {invoiceNumber !== null && totalAmount !== null && (
              <div className="flex flex-col gap-2 items-center">
                <span className="text-xl">
                  {t('payment.paymentId', {
                    paymentId: invoiceNumber,
                  })}
                </span>
                <span className="text-xl">
                  {t('payment.amountPaid', {
                    amountPaid: formatMoney(totalAmount),
                  })}
                </span>
              </div>
            )}
          </div>

          <ActionButton title={t('payment.viewApplication')} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col items-center gap-12">
        <FailureIcon />

        <div className="flex flex-col gap-6 items-center">
          <p className="font-bold text-2xl">{t('payment.failureMessageTitle')}</p>
          <p className="text-xl whitespace-pre-line text-center">{t('payment.failureMessageDescription')}</p>
        </div>
        <ActionButton title={t('payment.returnToApplication')} />
      </div>
    </Layout>
  );
}

const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <FlowableTaskLayout>
      <div className="absolute top-0 left-0 flex items-center justify-center w-full h-screen">{children}</div>
    </FlowableTaskLayout>
  );
};

const ActionButton = ({ title }: { title: string }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { processInstanceId } = useParams({
    from: '/_auth/processes/$processInstanceId/task/$taskId/payment/',
  });

  return (
    <Button
      onClick={() => {
        navigate({
          to: '/processes/$processInstanceId',
          params: { processInstanceId },
          search: { from: 'task', version: undefined },
        });
      }}
    >
      {title}
      <ArrowBackFilled className={i18n.language === 'en' ? 'rotate-180' : ''} />
    </Button>
  );
};

const SuccessIcon = () => {
  return (
    <div className="bg-positive-high w-20 h-20 rounded-full flex items-center justify-center">
      <CheckFilled className="w-8 h-8 text-positive-contrast" />
    </div>
  );
};

const FailureIcon = () => {
  return (
    <div className="bg-negative-medium w-20 h-20 rounded-full flex items-center justify-center">
      <CloseFilled className="w-8 h-8 text-negative-contrast" />
    </div>
  );
};
